class FormdCartAddons extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') {
      return;
    }

    this.dataset.initialized = 'true';

    this.addEventListener(
      'click',
      this.handleClick.bind(this)
    );
  }

  async handleClick(event) {
    const upgradeButton = event.target.closest(
      '[data-formd-upgrade-button]'
    );

    if (upgradeButton) {
      await this.handleBundleUpgrade(
        upgradeButton
      );

      return;
    }

    const addonButton = event.target.closest(
      '[data-formd-addon-button]'
    );

    if (!addonButton || addonButton.disabled) {
      return;
    }

    if (
      addonButton.getAttribute('aria-busy') ===
      'true'
    ) {
      return;
    }

    await this.handleAddon(addonButton);
  }


  /* =========================================
     CONTROL PRINCIPAL DEL UPGRADE
     ========================================= */

  async handleBundleUpgrade(button) {
    if (
      button.disabled ||
      button.getAttribute('aria-busy') === 'true'
    ) {
      return;
    }

    const upgradeMode =
      button.dataset.upgradeMode || 'standalone';

    const originalLineKey =
      button.dataset.originalLineKey;

    const journalVariantId = Number(
      button.dataset.journalVariantId
    );

    const journalProductId = Number(
      button.dataset.journalProductId
    );

    const penVariantId = Number(
      button.dataset.penVariantId
    );

    const dealId =
      button.dataset.bundleDealId;

    const bundleVersion =
      button.dataset.bundleVersion;

    const bundleId =
      button.dataset.bundleId;

    const giftId =
      button.dataset.giftId;

    const buttonTextElement =
      button.querySelector(
        '[data-formd-upgrade-text]'
      );

    const originalButtonText =
      buttonTextElement?.textContent.trim() ||
      'UPGRADE MY BUNDLE';

    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    if (buttonTextElement) {
      buttonTextElement.textContent =
        'UPGRADING…';
    }

    this.setUpgradeMessage(
      'Applying your bundle and free gifts…'
    );

    try {
      this.validateRequiredData({
        originalLineKey,
        journalVariantId,
        journalProductId,
        penVariantId,
        dealId,
        bundleVersion,
        bundleId,
        giftId
      });

      let finalState;

      if (upgradeMode === 'restore') {
        finalState = await this.restoreBundle({
          originalLineKey,
          journalVariantId,
          penVariantId,
          bundleId,
          giftId
        });
      } else {
        finalState =
          await this.upgradeStandaloneJournal({
            originalLineKey,
            journalVariantId,
            journalProductId,
            penVariantId,
            dealId,
            bundleVersion,
            bundleId,
            giftId
          });
      }

      this.setUpgradeMessage(
        'Bundle upgraded successfully.'
      );

      await this.refreshDrawer(finalState);
    } catch (error) {
      console.error(
        '[FORMD bundle upgrade]',
        error
      );

      button.disabled = false;
      button.removeAttribute('aria-busy');

      if (buttonTextElement) {
        buttonTextElement.textContent =
          originalButtonText;
      }

      this.setUpgradeMessage(
        error.message ||
        'The bundle could not be upgraded. Please try again.'
      );
    }
  }

  validateRequiredData({
    originalLineKey,
    journalVariantId,
    journalProductId,
    penVariantId,
    dealId,
    bundleVersion,
    bundleId,
    giftId
  }) {
    if (
      !originalLineKey ||
      !journalVariantId ||
      !journalProductId ||
      !penVariantId ||
      !dealId ||
      !bundleVersion ||
      !bundleId ||
      !giftId
    ) {
      throw new Error(
        'Required bundle information is missing.'
      );
    }
  }


  /* =========================================
     RESTAURAR BUNDLE REDUCIDO DE 2 A 1
     ========================================= */

  async restoreBundle({
    originalLineKey,
    journalVariantId,
    penVariantId,
    bundleId,
    giftId
  }) {
    /*
      La línea todavía conserva sus propiedades
      originales de Kaching.

      Solo restauramos la cantidad del journal a 2.
      Esto permite que Kaching reactive el descuento
      y vuelva a convertir los pens en regalos.
    */

    const restoreResponse =
      await this.changeItem(
        originalLineKey,
        2
      );

    if (!restoreResponse.ok) {
      const errorData =
        await this.safeJson(restoreResponse);

      throw new Error(
        errorData?.description ||
        errorData?.message ||
        'The existing bundle could not be restored.'
      );
    }

    /*
      Damos un momento a Kaching para recalcular
      el descuento y las relaciones del bundle.
    */

    await this.wait(250);

    const restoredCart = await this.getCart();

    const validation = this.validateBundle({
      cart: restoredCart,
      journalVariantId,
      penVariantId,
      bundleId,
      giftId
    });

    if (!validation.isValid) {
      /*
        Si Kaching no restauró correctamente
        el bundle, regresamos el journal a 1.
      */

      await this.changeItem(
        originalLineKey,
        1
      );

      throw new Error(validation.message);
    }

    /*
      Pedimos la sección actualizada después de
      confirmar que el descuento fue aplicado.
    */

    return this.getUpdatedDrawerState();
  }


  /* =========================================
     CONVERTIR JOURNAL INDIVIDUAL EN BUNDLE
     ========================================= */

  async upgradeStandaloneJournal({
    originalLineKey,
    journalVariantId,
    journalProductId,
    penVariantId,
    dealId,
    bundleVersion,
    bundleId,
    giftId
  }) {
    /*
      Cada bundle necesita un identificador propio.

      No se puede utilizar un valor fijo porque
      Shopify podría combinarlo con otro bundle.
    */

    const kachingGroupId =
      this.generateKachingGroupId();

    const journalProperties = {
      __kaching_bundles: JSON.stringify({
        deal: dealId,
        pp: journalProductId,
        main: true,
        v: bundleVersion,
        id: kachingGroupId,
        bid: bundleId
      })
    };

    const penProperties = {
      __kaching_bundles: JSON.stringify({
        id: kachingGroupId,
        deal: dealId,
        v: bundleVersion,
        gift: giftId
      })
    };

    /*
      Añadir primero el bundle nuevo.

      El journal individual no se elimina hasta
      confirmar que el nuevo bundle funciona.
    */

    const addResponse = await fetch(
      `${window.Shopify.routes.root}cart/add.js`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: journalVariantId,
              quantity: 2,
              properties: journalProperties
            },
            {
              id: penVariantId,
              quantity: 2,
              properties: penProperties
            }
          ]
        })
      }
    );

    if (!addResponse.ok) {
      const errorData =
        await this.safeJson(addResponse);

      throw new Error(
        errorData?.description ||
        errorData?.message ||
        'Kaching could not create the bundle.'
      );
    }

    await this.wait(250);

    const upgradedCart = await this.getCart();

    const validation = this.validateBundle({
      cart: upgradedCart,
      journalVariantId,
      penVariantId,
      bundleId,
      giftId,
      groupId: kachingGroupId
    });

    if (!validation.isValid) {
      await this.rollbackNewBundle({
        journalVariantId,
        penVariantId,
        bundleId,
        giftId,
        groupId: kachingGroupId
      });

      throw new Error(validation.message);
    }

    /*
      Ya comprobamos que el bundle tiene:

      - 2 journals
      - descuento aplicado
      - 2 Signature Pens
      - pens completamente gratis

      Ahora eliminamos el journal individual.
    */

    const removeResponse =
      await this.changeItem(
        originalLineKey,
        0
      );

    if (!removeResponse.ok) {
      await this.rollbackNewBundle({
        journalVariantId,
        penVariantId,
        bundleId,
        giftId,
        groupId: kachingGroupId
      });

      throw new Error(
        'The original journal could not be replaced.'
      );
    }

    return removeResponse.json();
  }


  /* =========================================
     VALIDACIÓN DEL BUNDLE
     ========================================= */

  validateBundle({
    cart,
    journalVariantId,
    penVariantId,
    bundleId,
    giftId,
    groupId = null
  }) {
    const bundleJournal = cart.items.find(
      (item) => {
        const data =
          this.getKachingData(item);

        if (
          Number(item.variant_id) !==
          journalVariantId
        ) {
          return false;
        }

        if (
          data?.main !== true ||
          data?.bid !== bundleId
        ) {
          return false;
        }

        if (
          groupId &&
          data?.id !== groupId
        ) {
          return false;
        }

        return true;
      }
    );

    if (!bundleJournal) {
      return {
        isValid: false,
        message:
          'Kaching did not recognize the two-journal bundle.'
      };
    }

    const detectedGroupId =
      bundleJournal
        ? this.getKachingData(bundleJournal)?.id
        : null;

    const bundlePens = cart.items.find(
      (item) => {
        const data =
          this.getKachingData(item);

        if (
          Number(item.variant_id) !==
          penVariantId
        ) {
          return false;
        }

        if (data?.gift !== giftId) {
          return false;
        }

        if (
          detectedGroupId &&
          data?.id !== detectedGroupId
        ) {
          return false;
        }

        return true;
      }
    );

    if (
      Number(bundleJournal.quantity) !== 2
    ) {
      return {
        isValid: false,
        message:
          'The bundle did not receive two journals.'
      };
    }

    if (
      Number(bundleJournal.final_line_price) >=
      Number(bundleJournal.original_line_price)
    ) {
      return {
        isValid: false,
        message:
          'The bundle discount was not applied.'
      };
    }

    if (!bundlePens) {
      return {
        isValid: false,
        message:
          'The free Signature Pens were not added.'
      };
    }

    if (
      Number(bundlePens.quantity) !== 2
    ) {
      return {
        isValid: false,
        message:
          'The bundle did not receive two Signature Pens.'
      };
    }

    if (
      Number(bundlePens.final_line_price) !== 0
    ) {
      return {
        isValid: false,
        message:
          'The Signature Pens were not made free.'
      };
    }

    return {
      isValid: true,
      message: ''
    };
  }

  getKachingData(item) {
    const rawData =
      item?.properties?.__kaching_bundles;

    if (!rawData) {
      return null;
    }

    if (typeof rawData === 'object') {
      return rawData;
    }

    try {
      return JSON.parse(rawData);
    } catch (error) {
      console.warn(
        '[FORMD] Invalid Kaching data:',
        rawData
      );

      return null;
    }
  }


  /* =========================================
     CREAR IDENTIFICADOR DINÁMICO
     ========================================= */

  generateKachingGroupId() {
    const characters =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

    let result = '';

    if (
      window.crypto &&
      window.crypto.getRandomValues
    ) {
      const randomValues =
        new Uint32Array(4);

      window.crypto.getRandomValues(
        randomValues
      );

      for (let index = 0; index < 4; index += 1) {
        result += characters[
          randomValues[index] %
          characters.length
        ];
      }

      return result;
    }

    for (let index = 0; index < 4; index += 1) {
      result += characters[
        Math.floor(
          Math.random() *
          characters.length
        )
      ];
    }

    return result;
  }


  /* =========================================
     ROLLBACK DE BUNDLE NUEVO
     ========================================= */

  async rollbackNewBundle({
    journalVariantId,
    penVariantId,
    bundleId,
    giftId,
    groupId
  }) {
    try {
      let cart = await this.getCart();

      const giftLines = cart.items.filter(
        (item) => {
          const data =
            this.getKachingData(item);

          return (
            Number(item.variant_id) ===
              penVariantId &&
            data?.gift === giftId &&
            data?.id === groupId
          );
        }
      );

      /*
        Primero eliminamos los regalos.
      */

      for (const item of giftLines) {
        await this.changeItem(
          item.key,
          0
        );
      }

      cart = await this.getCart();

      const journalLines = cart.items.filter(
        (item) => {
          const data =
            this.getKachingData(item);

          return (
            Number(item.variant_id) ===
              journalVariantId &&
            data?.bid === bundleId &&
            data?.id === groupId
          );
        }
      );

      /*
        Después eliminamos el journal del
        bundle que no se pudo validar.
      */

      for (const item of journalLines) {
        await this.changeItem(
          item.key,
          0
        );
      }
    } catch (rollbackError) {
      console.error(
        '[FORMD bundle rollback]',
        rollbackError
      );
    }
  }


  /* =========================================
     ADD-ONS NORMALES
     ========================================= */

  async handleAddon(button) {
    const variantId =
      button.dataset.variantId;

    button.setAttribute(
      'aria-busy',
      'true'
    );

    this.setMessage(
      'Adding add-on…'
    );

    try {
      const response =
        await this.addItem(variantId);

      if (!response.ok) {
        const errorData =
          await this.safeJson(response);

        throw new Error(
          errorData?.description ||
          errorData?.message ||
          'Cart request failed.'
        );
      }

      const state = await response.json();

      await this.refreshDrawer(state);
    } catch (error) {
      console.error(
        '[FORMD cart add-ons]',
        error
      );

      button.removeAttribute('aria-busy');

      this.setMessage(
        'Something went wrong. Please try again.'
      );
    }
  }

  addItem(variantId) {
    return fetch(
      `${window.Shopify.routes.root}cart/add.js`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1
            }
          ],
          sections: [
            'cart-drawer-section'
          ],
          sections_url:
            window.location.pathname
        })
      }
    );
  }


  /* =========================================
     PETICIONES DEL CARRITO
     ========================================= */

  changeItem(lineKey, quantity) {
    return fetch(
      `${window.Shopify.routes.root}cart/change.js`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: lineKey,
          quantity,
          sections: [
            'cart-drawer-section'
          ],
          sections_url:
            window.location.pathname
        })
      }
    );
  }

  async getCart() {
    const response = await fetch(
      `${window.Shopify.routes.root}cart.js`,
      {
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        'Cart could not be loaded.'
      );
    }

    return response.json();
  }

  async getUpdatedDrawerState() {
    const response = await fetch(
      `${window.Shopify.routes.root}cart.js`,
      {
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        'The updated cart could not be loaded.'
      );
    }

    const cart = await response.json();

    const sectionResponse = await fetch(
      `${window.Shopify.routes.root}?sections=cart-drawer-section`,
      {
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!sectionResponse.ok) {
      return cart;
    }

    const sections =
      await sectionResponse.json();

    cart.sections = sections;

    return cart;
  }


  /* =========================================
     ACTUALIZAR EL DRAWER
     ========================================= */

  async refreshDrawer(state) {
    let sectionHTML =
      state?.sections?.[
        'cart-drawer-section'
      ];

    if (!sectionHTML) {
      const sectionResponse = await fetch(
        `${window.Shopify.routes.root}?sections=cart-drawer-section`,
        {
          headers: {
            Accept: 'application/json'
          }
        }
      );

      if (sectionResponse.ok) {
        const sections =
          await sectionResponse.json();

        sectionHTML =
          sections?.[
            'cart-drawer-section'
          ];
      }
    }

    if (sectionHTML) {
      const parsedDocument =
        new DOMParser().parseFromString(
          sectionHTML,
          'text/html'
        );

      const incomingDrawer =
        parsedDocument.querySelector(
          '.cart-drawer__inner'
        );

      const currentDrawer =
        document.querySelector(
          '#cart-drawer .cart-drawer__inner'
        );

      if (
        incomingDrawer &&
        currentDrawer
      ) {
        currentDrawer.replaceWith(
          incomingDrawer
        );
      }
    }

    const cart = await this.getCart();

    this.updateCartBubble(
      cart.item_count
    );

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          cart,
          source: 'formd-cart-addons'
        }
      })
    );
  }


  /* =========================================
     BURBUJA DEL CARRITO
     ========================================= */

  updateCartBubble(itemCount) {
    const bubble =
      document.querySelector(
        '[ref="cartBubble"]'
      );

    const count =
      document.querySelector(
        '[ref="cartBubbleCount"]'
      );

    if (count) {
      count.textContent = itemCount;

      count.classList.toggle(
        'hidden',
        itemCount === 0
      );
    }

    if (bubble) {
      bubble.classList.toggle(
        'visually-hidden',
        itemCount === 0
      );
    }
  }


  /* =========================================
     MENSAJES
     ========================================= */

  setUpgradeMessage(text) {
    const message = this.querySelector(
      '[data-formd-upgrade-message]'
    );

    if (message) {
      message.textContent = text;
    }
  }

  setMessage(text) {
    const message = this.querySelector(
      '[data-formd-addons-message]'
    );

    if (message) {
      message.textContent = text;
    }
  }


  /* =========================================
     UTILIDADES
     ========================================= */

  wait(milliseconds) {
    return new Promise((resolve) => {
      window.setTimeout(
        resolve,
        milliseconds
      );
    });
  }

  async safeJson(response) {
    try {
      return await response.json();
    } catch (error) {
      return null;
    }
  }
}

if (
  !customElements.get(
    'formd-cart-addons'
  )
) {
  customElements.define(
    'formd-cart-addons',
    FormdCartAddons
  );
}